import { MeetingFormats } from "@/constants"
import { DataContext, QueryContext } from "@/contexts"
import { getSchoolsAutocomplete } from "@/domain"
import { formatDateUTC, parseDateUTC } from "@/helpers/dates"
import { useContext, useEffect, useMemo, useState } from "react"
import { defaultLocationValidation } from "../../../fields/location"
import { autocompleteEducatables } from "../../configurations/domain"
import { FormFieldNames } from "./constants"

export const useCreateInitialValues = (schedules, schoolId, educatableId) => {
  const { getHookState } = useContext(QueryContext)
  const [initialSchoolComplete, setInitialSchoolComplete] = useState(false)
  const [initialEducatableComplete, setInitialEducatableComplete] = useState(false)
  const { request: requestSchool, loading: schoolLoading } = getHookState(getSchoolsAutocomplete())
  const { request: requestEducatable, loading: educatableLoading } = getHookState(autocompleteEducatables)
  const { schools, educatables: educatable } = useContext(DataContext)

  useEffect(() => {
    const fetchInitialData = async () => {
      if (schoolId && requestSchool && !schoolLoading && !initialSchoolComplete) {
        await requestSchool({ params: { id: schoolId } })
        setInitialSchoolComplete(true)
      }
      if (educatableId && requestEducatable && !educatableLoading && !initialEducatableComplete) {
        await requestEducatable({ params: { id: educatableId } })
        setInitialEducatableComplete(true)
      }
    }
    fetchInitialData()
  }, [
    schoolId,
    educatableId,
    requestSchool,
    requestEducatable,
    schoolLoading,
    educatableLoading,
    initialSchoolComplete,
    initialEducatableComplete,
  ])

  const initialValues = useMemo(() => {
    const [schedule = {}] = schedules
    const link = schedule.meeting_format === MeetingFormats.Online ? schedule.meeting_info : ""

    return {
      [FormFieldNames.School]: schedule?.school ?? (initialSchoolComplete ? schools?.[0] : undefined),
      [FormFieldNames.Educatable]: schedule?.educatable ?? (initialEducatableComplete ? educatable?.[0] : undefined),
      [FormFieldNames.Subject]: schedule?.program,
      [FormFieldNames.StartOn]: schedule?.start_on ? new Date(schedule.start_on) : null,
      [FormFieldNames.EndOn]: schedule?.end_on ? new Date(schedule.end_on) : null,
      [FormFieldNames.MeetingFormat]: schedule?.meeting_format || MeetingFormats.LessonSpace,
      [FormFieldNames.Address1]: schedule?.address_1,
      [FormFieldNames.Address2]: schedule?.address_2,
      [FormFieldNames.City]: schedule?.city,
      [FormFieldNames.State]: schedule?.state,
      [FormFieldNames.Zip]: schedule?.zip,
      [FormFieldNames.Link]: link,
      [FormFieldNames.ScheduleNote]: schedule?.note || "",
      [FormFieldNames.MaxSessions]: schedule?.max_sessions,
      [FormFieldNames.Duration]: schedule?.max_duration,
      [FormFieldNames.SendReportsTo]: Array.isArray(schedule?.additional_emails)
        ? schedule.additional_emails.join(", ")
        : schedule?.additional_emails,
      [FormFieldNames.NotifyAboutUpcoming]: Boolean(schedule?.notify_parent),
      [FormFieldNames.Rate]: schedule?.school_rate,
    }
  }, [schedules, initialSchoolComplete, schools, initialEducatableComplete, educatable])

  return { initialValues, loading: schoolLoading || educatableLoading }
}

export const useEditInitialValues = schedules => {
  const { schools, educatables: educatable } = useContext(DataContext)

  const multipleValues = useMemo(() => {
    if (schedules.length <= 1) {
      return Object.fromEntries(Object.values(FormFieldNames).map(field => [field, false]))
    }

    const getLink = schedule => (schedule.meeting_format === MeetingFormats.Online ? schedule.meeting_info : "")
    const getEmail = schedule =>
      Array.isArray(schedule.additional_emails)
        ? schedule.additional_emails.join(", ")
        : schedule.additional_emails || ""

    return Object.values(FormFieldNames).reduce((acc, field) => {
      acc[field] = schedules.some((sch, i) => {
        if (i === schedules.length - 1) return false
        const nextSch = schedules[i + 1]
        switch (field) {
          case FormFieldNames.School:
            return sch.school.id !== nextSch.school.id
          case FormFieldNames.Educatable:
            return sch.educatable.id !== nextSch.educatable.id
          case FormFieldNames.Subject:
            return sch.program.id !== nextSch.program.id
          case FormFieldNames.Link:
            return getLink(sch) !== getLink(nextSch)
          case FormFieldNames.SendReportsTo:
            return getEmail(sch) !== getEmail(nextSch)
          case FormFieldNames.ScheduleNote:
            return (sch.note || "") !== (nextSch.note || "")
          default:
            return sch[field] !== nextSch[field]
        }
      })
      return acc
    }, {})
  }, [schedules])

  const initialValues = useMemo(() => {
    const [schedule = {}] = schedules
    return Object.values(FormFieldNames).reduce((acc, field) => {
      if (multipleValues[field]) {
        acc[field] = field === FormFieldNames.ScheduleNote ? "" : null
      } else {
        switch (field) {
          case FormFieldNames.School:
            acc[field] = schedule?.school ?? schools?.[0]
            break
          case FormFieldNames.Educatable:
            acc[field] = schedule?.educatable ?? educatable?.[0]
            break
          case FormFieldNames.Subject:
            acc[field] = schedule?.program
            break
          case FormFieldNames.StartOn:
          case FormFieldNames.EndOn:
            acc[field] = schedule[field] ? parseDateUTC(schedule[field]) : null
            break
          case FormFieldNames.MeetingFormat:
            acc[field] = schedule?.meeting_format || MeetingFormats.LessonSpace
            break
          case FormFieldNames.Link:
            acc[field] = schedule.meeting_format === MeetingFormats.Online ? schedule.meeting_info : null
            break
          case FormFieldNames.SendReportsTo:
            acc[field] = Array.isArray(schedule?.additional_emails)
              ? schedule.additional_emails.join(", ")
              : schedule?.additional_emails || ""
            break
          case FormFieldNames.NotifyAboutUpcoming:
            acc[field] = Boolean(schedule?.notify_parent)
            break
          case FormFieldNames.ScheduleNote:
            acc[field] = schedule?.note || ""
            break
          case FormFieldNames.EnableTranscription:
            acc[field] = schedule?.enable_transcription
            break
          default:
            acc[field] = schedule[field] ?? null
        }
      }
      return acc
    }, {})
  }, [schedules, multipleValues, schools, educatable])

  return { initialValues, multipleValues }
}

export const mapFormFieldsToRequestFields = data => {
  return Object.entries(data).reduce((result, [key, value]) => {
    if (key === FormFieldNames.ScheduleNote) {
      result[key] = value || ""
      return result
    }

    if (value === undefined || value === null) return result

    switch (key) {
      case FormFieldNames.Educatable:
      case FormFieldNames.Subject:
      case FormFieldNames.School:
        if (value?.value || value?.id) {
          result[key === FormFieldNames.Subject ? "default_program_id" : `${key}_id`] = parseInt(
            value.value || value.id,
            10
          )
        }
        break
      case FormFieldNames.MaxSessions:
      case FormFieldNames.Rate:
        result[key] = parseFloat(value) || null
        break
      case FormFieldNames.Duration:
      case FormFieldNames.SendReportsTo:
        result[key] = value || null
        break
      case FormFieldNames.Link:
      case FormFieldNames.Address1:
      case FormFieldNames.Address2:
      case FormFieldNames.City:
      case FormFieldNames.State:
      case FormFieldNames.Zip:
        if (
          data[FormFieldNames.MeetingFormat] ===
          (key === FormFieldNames.Link ? MeetingFormats.Online : MeetingFormats.Offline)
        ) {
          result[key] = value
        }
        break
      case FormFieldNames.MeetingFormat:
      case FormFieldNames.NotifyAboutUpcoming:
        result[key] = value
        break
      case FormFieldNames.EndOn:
      case FormFieldNames.StartOn:
        result[key] = value ? formatDateUTC(value) : null
        break
      case FormFieldNames.EnableTranscription:
        result[key] = value
        break
    }
    return result
  }, {})
}

export const validateScheduleForm =
  ({ type, totalScheduledSessions }) =>
  values => {
    const errors = {}

    if (type === "create") {
      const createRequiredFields = [FormFieldNames.School, FormFieldNames.Educatable]
      createRequiredFields.forEach(field => {
        if (!values[field]) {
          errors[field] = "This field is required"
        }
      })
    }

    const commonRequiredFields = [
      FormFieldNames.Subject,
      FormFieldNames.StartOn,
      FormFieldNames.EndOn,
      FormFieldNames.Rate,
      FormFieldNames.MaxSessions,
      FormFieldNames.Duration,
    ]
    commonRequiredFields.forEach(field => {
      if (!values[field]) {
        errors[field] = "This field is required"
      }
    })

    if (values[FormFieldNames.Rate] && isNaN(parseFloat(values[FormFieldNames.Rate]))) {
      errors[FormFieldNames.Rate] = "This field value must be numeric"
    }

    if (values[FormFieldNames.MaxSessions]) {
      const maxSessions = parseInt(values[FormFieldNames.MaxSessions], 10)
      if (isNaN(maxSessions)) {
        errors[FormFieldNames.MaxSessions] = "This field value must be numeric"
      } else if (maxSessions < 0) {
        errors[FormFieldNames.MaxSessions] = "This field value must be positive"
      } else if (type === "edit" && maxSessions < totalScheduledSessions) {
        errors[
          FormFieldNames.MaxSessions
        ] = `Cannot set limit of maximum sessions less than scheduled sessions: ${totalScheduledSessions}`
      }
    }

    return {
      ...errors,
      ...defaultLocationValidation(values),
    }
  }

export const multipleValidateScheduleForm = values => {
  const errors = {}

  if (values[FormFieldNames.Rate] && isNaN(parseFloat(values[FormFieldNames.Rate]))) {
    errors[FormFieldNames.Rate] = "This field value must be numeric"
  }

  if (values[FormFieldNames.MaxSessions]) {
    const maxSessions = parseInt(values[FormFieldNames.MaxSessions], 10)
    if (isNaN(maxSessions)) {
      errors[FormFieldNames.MaxSessions] = "This field value must be numeric"
    } else if (maxSessions < 0) {
      errors[FormFieldNames.MaxSessions] = "This field value must be positive"
    }
  }

  return errors
}
